import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { IonIcon } from "@ionic/react";
import { alertCircleSharp, checkmarkCircleSharp, informationCircleSharp } from "ionicons/icons";
import { ShippingMethod, ShippingMethodType } from "@core/types";
import { prettifyNumbers } from "../../../utils/prettifyNumbers";
import { useGetMinimumOrderAmount } from "../../../hooks";
import { useCartData } from "../../../providers/cartDataStore";
import { useOrderData } from "../../../providers/orderDataStore";
import { useConfig } from "../../../providers/config";
import { useMarketMode } from "../../../providers/marketMode";
import { getDeliveryStatusText } from "../../../utils/getDeliveryStatusText";
import { isFastCourierFeatureEnabled } from "../../../providers/address/taxiMasterShippingUtils";
import { useGetDeliveryShippingFeePerKg } from "../../../hooks/useGetDeliveryShippingFeePerKg";

export type OrderProgressTextProps = {
  enabledShippingMethods: ShippingMethod[];
};

const FREE_DELIVERY_COST = 0;

export const OrderProgressText = ({ enabledShippingMethods }: OrderProgressTextProps) => {
  const config = useConfig();
  const { t } = useTranslation();
  const { mode } = useMarketMode();
  const { shippingMethod } = useOrderData();
  const { cartSubtotalValue } = useCartData();
  const { minimumOrderAmount, amountToRaise, isMinimumAmountReached } = useGetMinimumOrderAmount();

  const isTaxiMasterEnabled = isFastCourierFeatureEnabled(config);
  const isExpressDeliveryEnabled = !!config.isExpressDeliveryEnabled;
  const isExpressDeliveryTurned = isExpressDeliveryEnabled && mode === "express";
  const isTaxiMasterTurned = isTaxiMasterEnabled && shippingMethod?.methodType === ShippingMethodType.taxiMaster;

  const feePerKg = useGetDeliveryShippingFeePerKg();
  const currentShippingCost = shippingMethod?.cost ?? "0";
  const totalDeliveryCost = (Number(currentShippingCost) || 0) + feePerKg;

  const freeDeliveryThreshold = useMemo(() => {
    const freeDeliveryMethods = enabledShippingMethods.filter((method) => {
      const shippingCost = Number(method.cost ?? 0);

      return method.methodType !== ShippingMethodType.taxiMaster && shippingCost === FREE_DELIVERY_COST;
    });

    const thresholds = freeDeliveryMethods
      .map((method) => Number(method.minAmount))
      .filter((amount) => {
        return !Number.isNaN(amount) && amount > minimumOrderAmount;
      });

    return thresholds.length > 0 ? Math.min(...thresholds) : null;
  }, [enabledShippingMethods, minimumOrderAmount]);

  const amountToFreeDelivery =
    freeDeliveryThreshold != null ? prettifyNumbers(Math.max(0, freeDeliveryThreshold - cartSubtotalValue)) : null;

  const weightFeeExplanation = t("Weight fee explanation", {
    maxWeight: config.maxWeightForFeeKG,
    step: config.maxWeightFeeStep,
    fee: config.extraWeightFee ?? 0,
  });

  const isInPaidDeliveryRange =
    isMinimumAmountReached && freeDeliveryThreshold != null && cartSubtotalValue < freeDeliveryThreshold;

  const messages = useMemo(() => {
    const result: { message: string; id: number }[] = [];

    if (!isMinimumAmountReached) {
      result.push({
        message: t("To place an order, add items worth {{amount}} more", { amount: `${amountToRaise}₽` }),
        id: 1,
      });
    }

    const hasPaidDelivery = !!parseInt(currentShippingCost, 10) || feePerKg > 0;

    if (
      isInPaidDeliveryRange &&
      hasPaidDelivery &&
      !isTaxiMasterTurned &&
      amountToFreeDelivery &&
      !isExpressDeliveryTurned
    ) {
      result.push({
        message:
          feePerKg > 0
            ? `${t("To free delivery", { amount: `${amountToFreeDelivery}₽` })}\n${weightFeeExplanation}`
            : t("Add {{amount}} more and get free delivery!", {
                amount: `${amountToFreeDelivery}₽`,
              }),
        id: 2,
      });
    }

    if (
      freeDeliveryThreshold !== null &&
      cartSubtotalValue >= freeDeliveryThreshold &&
      !isTaxiMasterTurned &&
      !isExpressDeliveryTurned
    ) {
      result.push({
        message:
          feePerKg > 0
            ? weightFeeExplanation
            : getDeliveryStatusText(currentShippingCost, feePerKg, t("Free shipping"), t("Delivery cost")),
        id: 3,
      });
    }

    return result;
  }, [
    freeDeliveryThreshold,
    cartSubtotalValue,
    isTaxiMasterTurned,
    t,
    currentShippingCost,
    feePerKg,
    weightFeeExplanation,
  ]);

  if (!messages.length) {
    return null;
  }

  const isSuccessState =
    totalDeliveryCost === 0 &&
    freeDeliveryThreshold !== null &&
    cartSubtotalValue >= freeDeliveryThreshold &&
    !isTaxiMasterTurned &&
    !isExpressDeliveryTurned;

  return (
    <div className="w-full flex items-center gap-6 text-sm border-b border-gray-200">
      <div className={cx("flex gap-1 justify-between items-center py-5 px-2 w-full", isSuccessState && "text-green")}>
        <div className="flex items-center gap-1">
          <IonIcon
            icon={
              isSuccessState ? checkmarkCircleSharp : isMinimumAmountReached ? informationCircleSharp : alertCircleSharp
            }
            className={cx(
              isSuccessState && "!text-green-400",
              !isMinimumAmountReached && "text-red-500",
              isMinimumAmountReached && "text-primary",
            )}
          />
          {messages.map(({ message, id }) => (
            <div
              className={cx(
                "font-bold whitespace-pre-line",
                isSuccessState && "!text-green-400",
                !isMinimumAmountReached && "text-red-500",
                isMinimumAmountReached && "text-primary",
              )}
              key={id}
            >
              {message}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
